import { Component, OnInit } from "@angular/core";
import {
  animate,
  query,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { ActivatedRoute } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { GlobalVariable } from "../app.component";
import { DataService, Response } from "../dataService/data.service";
import { CommonService } from "../services/common.service";

type Sentiment = "positive" | "negative";

// Keyword/word entries always carry an id (UUID string from DB, or null for
// the static-config fallback and the OTHERS sentinel). Word entries also
// carry keyword_id so the consumer flow can scope Screen 3 intents to the
// keywords picked on Screen 2.
type KeywordItem = { id: string | null; text: string };
type WordItem = { id: string | null; text: string; keyword_id: string | null };

interface BootstrapResponse {
  vertical: string;
  business_name: string;
  business_logo: string | null;
  contact_name: string;
  consumer_welcome_message: string | null;
  show_powered_by_logo: boolean;
  outlet_name: string | null;
  google_review_url: string | null;
  source?: "db" | "config";
  keywords: { positive: KeywordItem[]; negative: KeywordItem[] };
  word_pools: { positive: WordItem[]; negative: WordItem[] };
  others_word_pool: { positive: string[]; negative: string[] };
  others_keyword: string;

  // Set when the merchant pinned AI review keywords while sending the invite.
  // `keywords`/`word_pools` are then narrowed to that pinned set and the
  // selection arrives pre-made, so Screen 2 is read-only.
  locked_keywords?: boolean;
  sentiment?: Sentiment;
  selected_keywords?: string[];
  selected_keyword_ids?: string[];
  selected_words?: string[];
  selected_word_ids?: string[];
  others_text?: string;
}

@Component({
  selector: "app-ai-review-experience-legacy",
  templateUrl: "./ai-review-experience-legacy.component.html",
  styleUrls: ["./ai-review-experience-legacy.component.scss"],
  animations: [
    trigger("slideStep", [
      transition(":increment", [
        query(
          ":enter",
          [
            style({ transform: "translateX(24px)", opacity: 0 }),
            animate(
              "220ms cubic-bezier(.22,.61,.36,1)",
              style({ transform: "translateX(0)", opacity: 1 })
            ),
          ],
          { optional: true }
        ),
      ]),
      transition(":decrement", [
        query(
          ":enter",
          [
            style({ transform: "translateX(-24px)", opacity: 0 }),
            animate(
              "220ms cubic-bezier(.22,.61,.36,1)",
              style({ transform: "translateX(0)", opacity: 1 })
            ),
          ],
          { optional: true }
        ),
      ]),
    ]),
  ],
})
export class AiReviewExperienceLegacyComponent implements OnInit {
  private BASE_URL = GlobalVariable;

  // Routing / loading
  slug_value: string = "";
  outlet_id: string | null = null;
  loading: boolean = true;
  loadError: boolean = false;

  // Bootstrap-driven state (all 4 screens powered from one payload)
  config: BootstrapResponse | null = null;

  // Step state. Only one screen mounted at a time so the slide animation
  // has a clean :enter target.
  step: 1 | 2 | 3 | 4 = 1;

  // User selections
  sentiment: Sentiment | null = null;
  selectedKeywords: string[] = [];
  // Parallel ID tracking for analytics/capture. Populated only when bootstrap
  // returns {id,text} items (source==="db"). For legacy string-only config
  // fallback these stay empty arrays and the backend tolerates that.
  selectedKeywordIds: string[] = [];
  othersText: string = "";
  selectedWords: string[] = [];
  selectedWordIds: string[] = [];

  // AI-generated descriptor intents derived from `othersText`. Merged into
  // currentWordPool on Screen 3 so the user can pick them like any other
  // descriptor. Tracked separately so we don't lose them on a refetch.
  othersIntents: string[] = [];
  isLoadingOthersIntents: boolean = false;

  // Screen 4 state
  reviewDraft: string = "";
  additionalText: string = "";
  isGenerating: boolean = false;
  isRefining: boolean = false;

  // Inline "Anything to add?" editor state on Screen 4 (positive flow)
  isEditingAdditional: boolean = false;
  additionalDraft: string = "";

  // Negative-sentiment Screen 4 — free-text feedback (no AI)
  feedbackText: string = "";

  // Negative-sentiment thank-you mockup (no backend call yet)
  showThankYouModal: boolean = false;

  // Mirrors legacy review-improvement: when slug_has_feedback is true under a
  // feedback-only slug, the customer has already submitted once — skip the
  // entire flow and show the "already provided feedback" screen.
  isFeedbackAlreadyGiven: boolean = false;
  feedbackAlreadyGivenMessage: string =
    "Feedback already received. Our team is looking into it.";

  // Legacy slug fields — sourced from CommonService.commonData so the AI flow
  // mirrors what the legacy review-improvement component would have shown.
  welcomeMessage: string = "";
  thankYouMessage: string = "";
  consumerLink: string | null = null;
  feedbackScale: string | null = null;
  reviewClickSteps: number | null = null;
  reviewGateway: boolean = false;
  feedbackOnlyFlow: boolean = false;
  hasFeedback: boolean = false;
  slugHasFeedback: boolean = false;
  isShowThankYouScreen: boolean = false;
  submitInFlight: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private http: DataService,
    private toastr: ToastrService,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.route.params.forEach((val) => {
      this.slug_value = val["id"];
    });
    this.route.queryParamMap.subscribe((qp) => {
      this.outlet_id = qp.get("outlet_id");
    });

    // Mirror legacy review-improvement: pick up welcome/thank-you/consumer_link
    // /feedback_scale/review_click_steps/review_gateway/enable_external_review
    // from the shared slug response. The layout component fetches it just
    // before navigating here, so the cached value is replayed via getData().
    this.commonService.commonData.subscribe((data: any) => {
      if (!data || data.isApiError) return;
      this.welcomeMessage = data["consumer_welcome_message"] || "";
      this.thankYouMessage = data["consumer_thank_you_message"] || "";
      this.consumerLink = data["consumer_link"] || null;
      this.feedbackScale = data["feedback_scale"] || null;
      this.reviewClickSteps = data["review_click_steps"] ?? null;
      this.reviewGateway = !!data["review_gateway"];
      this.feedbackOnlyFlow = !!data["enable_external_review"];
      this.hasFeedback = !!data["has_feedback"];
      this.slugHasFeedback = !!data["slug_has_feedback"];
      if (!this.outlet_id && data["outlet_id"]) {
        this.outlet_id = data["outlet_id"];
      }
      // Legacy short-circuit: enable_external_review + slug_has_feedback means
      // this customer has already left feedback once for this slug. Skip the
      // rating UI and show the post-feedback screen immediately.
      // Ref: review-improvement.component.ts:257 + showThankYouScreen() L606.
      if (this.feedbackOnlyFlow && this.slugHasFeedback) {
        this.isFeedbackAlreadyGiven = true;
        this.isShowThankYouScreen = true;
      }
    });
    // Replay cached commonData if the layout already fetched it.
    this.commonService.getData();

    this.bootstrap();
  }

  // ---------------------------------------------------------------------
  // Bootstrap — single call powering all 4 screens
  // ---------------------------------------------------------------------
  private bootstrap(): void {
    this.loading = true;
    const payload: any = { slug: this.slug_value };
    if (this.outlet_id) payload.outlet_id = this.outlet_id;
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_BOOTSTRAP, payload)
      .subscribe(
        (res: Response) => {
          if (res.code === 200) {
            this.config =
              typeof res.data === "string" ? JSON.parse(res.data) : res.data;
            this.applyLockedSelection();
            this.loadError = false;
          } else {
            this.loadError = true;
          }
          this.loading = false;
        },
        () => {
          this.loadError = true;
          this.loading = false;
        }
      );
  }

  // Seed the merchant's invite-pinned selection. The backend has already
  // narrowed `keywords`/`word_pools` to the pinned set, so Screen 2 shows only
  // these chips and `isKeywordSelectionLocked` makes them read-only. Selection
  // is copied verbatim — NOT filtered against the catalogue — because a pinned
  // keyword the merchant later renamed or removed must still count.
  private applyLockedSelection(): void {
    if (!this.config?.locked_keywords) return;

    this.selectedKeywords = this.toArray<string>(
      this.config.selected_keywords
    ).filter((t) => !!t);
    this.selectedKeywordIds = this.toArray<string>(
      this.config.selected_keyword_ids
    ).filter((id) => !!id);
    this.selectedWords = this.toArray<string>(
      this.config.selected_words
    ).filter((t) => !!t);
    this.selectedWordIds = this.toArray<string>(
      this.config.selected_word_ids
    ).filter((id) => !!id);
    if (this.config.others_text) this.othersText = this.config.others_text;
  }

  // True when keywords were pinned to the review invite: the consumer sees the
  // merchant's selection and cannot change it.
  get isKeywordSelectionLocked(): boolean {
    return !!this.config?.locked_keywords && this.selectedKeywords.length > 0;
  }

  // ---------------------------------------------------------------------
  // Computed accessors used by template — no async, no flicker
  // ---------------------------------------------------------------------

  // Backend has been seen to send sentiment-bucketed arrays as a
  // double-encoded JSON string or an object-keyed map ({"0": "..."}).
  // Coerce any of those shapes back into a clean array so the UI never
  // crashes on a flaky payload.
  private toArray<T>(value: unknown): T[] {
    if (value == null) return [];
    if (Array.isArray(value)) return value as T[];
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? (parsed as T[]) : [];
      } catch {
        return [];
      }
    }
    if (typeof value === "object") {
      return Object.values(value as Record<string, T>);
    }
    return [];
  }

  // Look up the ID for a label among a list of items. Returns null for the
  // OTHERS sentinel (which has id=null) and for the static-config fallback.
  private idForLabel(
    items: Array<{ id: string | null; text: string }>,
    label: string
  ): string | null {
    for (const it of items) {
      if (it.text === label) return it.id;
    }
    return null;
  }

  // The sentiment-scoped keyword list for Screen 2. Defaults to positive
  // before the user picks a thumb so the chips render during the brief
  // transition between screens.
  private currentKeywords(): KeywordItem[] {
    if (!this.config) return [];
    const bucket = this.sentiment || "positive";
    return this.toArray<KeywordItem>(this.config.keywords?.[bucket]);
  }

  // Template-facing string list for Screen 2 chips.
  get keywordLabels(): string[] {
    return this.currentKeywords().map((k) => k.text);
  }

  // Screen 3 intents — narrowed by the keywords the user picked on Screen 2.
  // An intent belongs to the current view if:
  //   - its keyword_id is null (global within the sentiment), OR
  //   - its keyword_id matches one of the user's selectedKeywordIds.
  // If the user has picked only "Others" (no real keyword_ids), we fall back
  // to showing all sentiment-matching intents so the screen isn't empty.
  get currentWordPool(): string[] {
    if (!this.config || !this.sentiment) return [];
    const pool = this.toArray<WordItem>(
      this.config.word_pools?.[this.sentiment]
    );
    const pickedKeywordIds = new Set(this.selectedKeywordIds);
    const hasRealKeywordPick = pickedKeywordIds.size > 0;
    const filtered = pool.filter((w) => {
      if (w.keyword_id == null) return true;
      if (!hasRealKeywordPick) return true; // only "Others" picked → show all
      return pickedKeywordIds.has(w.keyword_id);
    });
    const base = filtered.map((w) => w.text);
    if (this.isOthersSelected) {
      const extrasRaw = this.config.others_word_pool?.[this.sentiment];
      const extras = this.toArray<string>(extrasRaw);
      // AI-derived intents from the user's "Others" note land at the front
      // so they read as the most relevant chips for what the user just typed.
      const ai = (this.othersIntents || []).filter((w) => !base.includes(w));
      const staticExtras = extras.filter(
        (w) => !base.includes(w) && !ai.includes(w)
      );
      return [...ai, ...base, ...staticExtras];
    }
    return base;
  }

  get isOthersSelected(): boolean {
    return (
      this.config != null &&
      this.selectedKeywords.includes(this.config.others_keyword)
    );
  }

  // Compact, de-duplicated list of the keywords + intents the user picked
  // through screens 2 and 3 — rendered as small pills on Screen 4 so the
  // user can see what shaped the draft without scrolling back.
  get selectionsRecap(): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    const push = (label: string) => {
      const v = (label || "").trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push(v);
    };
    for (const kw of this.selectedKeywords) {
      if (this.config && kw === this.config.others_keyword) {
        if (this.othersText.trim()) push(this.othersText.trim());
      } else {
        push(kw);
      }
    }
    for (const w of this.selectedWords) push(w);
    return out;
  }

  get canProceedFromScreen2(): boolean {
    if (this.selectedKeywords.length === 0) return false;
    if (this.isOthersSelected && this.othersText.trim().length === 0)
      return false;
    return true;
  }

  // ---------------------------------------------------------------------
  // Step navigation — instant, in-memory only
  // ---------------------------------------------------------------------
  goNext(): void {
    if (this.step === 1) {
      if (!this.sentiment) return;
      this.step = 2;
    } else if (this.step === 2) {
      if (!this.canProceedFromScreen2) return;
      // Fetch AI-derived intents for the "Others" free-text BEFORE stale-
      // filter pruning so freshly-suggested intents don't get culled.
      this.fetchOthersIntents();
      // Drop any stale word selections from a previous keyword pick that no
      // longer survive the current keyword filter on Screen 3.
      const visible = new Set(this.currentWordPool);
      this.selectedWords = this.selectedWords.filter((w) => visible.has(w));
      const visiblePool = this.sentiment
        ? this.toArray<WordItem>(this.config?.word_pools?.[this.sentiment])
        : [];
      const visibleIds = new Set(
        visiblePool
          .filter((w) => visible.has(w.text))
          .map((w) => w.id)
          .filter((id): id is string => id != null)
      );
      this.selectedWordIds = this.selectedWordIds.filter((id) =>
        visibleIds.has(id)
      );
      this.step = 3;
    } else if (this.step === 3) {
      this.step = 4;
      // Positive flows through to AI draft generation. Negative goes to
      // a free-text feedback screen instead — no AI call, no draft.
      if (this.sentiment !== "negative") {
        this.generate();
      }
    }
  }

  // Inline "Anything you'd like to add?" editor on Screen 4
  openAdditionalEditor(): void {
    this.additionalDraft = this.additionalText;
    this.isEditingAdditional = true;
  }

  cancelAdditionalEditor(): void {
    this.isEditingAdditional = false;
    this.additionalDraft = "";
  }

  saveAdditionalEditor(): void {
    const next = (this.additionalDraft || "").trim();
    const changed = next !== this.additionalText.trim();
    this.additionalText = next;
    this.isEditingAdditional = false;
    this.additionalDraft = "";
    if (changed) this.generate();
  }

  goBack(): void {
    if (this.step === 1) return;
    this.step = (this.step - 1) as 1 | 2 | 3 | 4;
  }

  // ---------------------------------------------------------------------
  // Screen 1 — sentiment
  // ---------------------------------------------------------------------
  selectSentiment(s: Sentiment): void {
    const sentimentChanged = this.sentiment !== s;
    this.sentiment = s;
    // Invite-pinned positive selection: keep it intact when the consumer picks
    // the matching sentiment (sentiment starts null, so the first tap would
    // otherwise read as a "change" and wipe the merchant's keywords). A pinned
    // set is positive-only, so thumbs-down falls through and clears as usual.
    if (this.config?.locked_keywords && s === "positive") {
      setTimeout(() => this.goNext(), 0);
      return;
    }
    // Sentiment switches the keyword chip set and the intent pool — clear any
    // prior screen 2/3 picks so users don't carry over selections that no
    // longer exist in the new bucket.
    if (sentimentChanged) {
      this.selectedKeywords = [];
      this.selectedKeywordIds = [];
      this.othersIntents = [];
    }
    this.selectedWords = [];
    this.selectedWordIds = [];
    // Advance immediately for a one-tap feel
    setTimeout(() => this.goNext(), 0);
  }

  // Ask the backend for descriptor intents derived from the user's "Others"
  // free-text. Called on the 2 → 3 transition; bails immediately when Others
  // wasn't picked or the text is empty. Fire-and-forget — the chips just
  // appear when the response lands; the UI stays usable in the meantime.
  private fetchOthersIntents(): void {
    if (!this.isOthersSelected) {
      this.othersIntents = [];
      return;
    }
    const note = (this.othersText || "").trim();
    if (!note || !this.sentiment) {
      this.othersIntents = [];
      return;
    }
    // Build the existing pool (without the AI intents) so the backend won't
    // suggest dupes of what's already on screen.
    const existingPool = this.toArray<WordItem>(
      this.config?.word_pools?.[this.sentiment]
    ).map((w) => w.text);
    const staticExtras = this.toArray<string>(
      this.config?.others_word_pool?.[this.sentiment]
    );
    const existing = [...existingPool, ...staticExtras];

    const payload: any = {
      slug: this.slug_value,
      sentiment: this.sentiment,
      others_text: note,
      existing_pool: existing,
      count: 5,
    };
    if (this.outlet_id) payload.outlet_id = this.outlet_id;

    this.isLoadingOthersIntents = true;
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_OTHERS_INTENTS, payload)
      .subscribe(
        (res: Response) => {
          this.isLoadingOthersIntents = false;
          if (res && res.code === 200) {
            const items =
              (res.data && (res.data["intents"] || res.data)) || [];
            this.othersIntents = Array.isArray(items)
              ? items.filter((s) => typeof s === "string" && s.length > 0)
              : [];
          }
        },
        () => {
          this.isLoadingOthersIntents = false;
        }
      );
  }

  // ---------------------------------------------------------------------
  // Screen 2 — keyword chips
  // ---------------------------------------------------------------------
  toggleKeyword(kw: string): void {
    // Invite-pinned selections are read-only.
    if (this.isKeywordSelectionLocked) return;
    const items = this.currentKeywords();
    const idx = this.selectedKeywords.indexOf(kw);
    if (idx >= 0) {
      this.selectedKeywords.splice(idx, 1);
      const id = this.idForLabel(items, kw);
      if (id != null) {
        const i = this.selectedKeywordIds.indexOf(id);
        if (i >= 0) this.selectedKeywordIds.splice(i, 1);
      }
    } else {
      this.selectedKeywords.push(kw);
      const id = this.idForLabel(items, kw);
      if (id != null && !this.selectedKeywordIds.includes(id)) {
        this.selectedKeywordIds.push(id);
      }
    }
  }

  isKeywordSelected(kw: string): boolean {
    return this.selectedKeywords.includes(kw);
  }

  // ---------------------------------------------------------------------
  // Screen 3 — descriptor word pool (max 3)
  // ---------------------------------------------------------------------
  toggleWord(w: string): void {
    const pool: WordItem[] = this.sentiment && this.config
      ? this.toArray<WordItem>(this.config.word_pools?.[this.sentiment])
      : [];
    const idx = this.selectedWords.indexOf(w);
    if (idx >= 0) {
      this.selectedWords.splice(idx, 1);
      const id = this.idForLabel(pool, w);
      if (id != null) {
        const i = this.selectedWordIds.indexOf(id);
        if (i >= 0) this.selectedWordIds.splice(i, 1);
      }
    } else {
      if (this.selectedWords.length >= 3) {
        this.toastr.info("You can select up to 3 words");
        return;
      }
      this.selectedWords.push(w);
      const id = this.idForLabel(pool, w);
      if (id != null && !this.selectedWordIds.includes(id)) {
        this.selectedWordIds.push(id);
      }
    }
  }

  isWordSelected(w: string): boolean {
    return this.selectedWords.includes(w);
  }

  // Descriptor values may be hyphenated (e.g. "visible-results") so they read as
  // a single concept downstream. Display them as plain words on the chip.
  displayWord(w: string): string {
    return (w || "").replace(/-/g, " ");
  }

  // ---------------------------------------------------------------------
  // Screen 4 — generate / refine / copy & redirect
  // ---------------------------------------------------------------------
  get combinedOthersText(): string {
    const parts = [this.othersText, this.additionalText]
      .map((p) => (p || "").trim())
      .filter((p) => p.length > 0);
    return parts.join(". ");
  }

  generate(): void {
    if (!this.sentiment) return;
    this.isGenerating = true;
    this.reviewDraft = "";
    const payload: any = {
      slug: this.slug_value,
      sentiment: this.sentiment,
      selected_keywords: this.selectedKeywords,
      selected_words: this.selectedWords,
      others_text: this.combinedOthersText,
    };
    if (this.outlet_id) payload.outlet_id = this.outlet_id;
    this.http.postjson(this.BASE_URL.AI_REVIEW_GENERATE, payload).subscribe(
      (res: Response) => {
        if (res.code === 200) {
          this.reviewDraft = res.data["review_draft"];
          // Capture the slug selection + initial draft (positive flow).
          // Fire-and-forget — no UI gating on this.
          this.captureSlugSelection({ posted_to_google: false });
        } else {
          this.toastr.error("Couldn't generate the review. Try again.");
        }
        this.isGenerating = false;
      },
      () => {
        this.toastr.error("Couldn't generate the review. Try again.");
        this.isGenerating = false;
      }
    );
  }

  // Fire-and-forget POST to record the consumer's slug-level selections at
  // each meaningful checkpoint (draft generated, posted to Google, negative
  // feedback submitted). The `extra` fields layer on top of the base payload.
  private captureSlugSelection(
    extra: Partial<{
      final_text: string;
      posted_to_google: boolean;
      feedback_text: string;
    }> = {}
  ): void {
    const payload: any = {
      slug: this.slug_value,
      sentiment: this.sentiment,
      selected_keyword_ids: this.selectedKeywordIds,
      selected_word_ids: this.selectedWordIds,
      others_text: this.combinedOthersText,
      generated_draft: this.reviewDraft,
      final_text: extra.final_text,
      posted_to_google: extra.posted_to_google,
      feedback_text: extra.feedback_text,
    };
    this.http
      .postjson(this.BASE_URL.AI_REVIEW_CAPTURE, payload)
      .subscribe(
        () => {},
        () => {}
      );
  }

  refine(mode: "refine" | "short" | "long"): void {
    if (!this.reviewDraft.trim()) return;
    this.isRefining = true;
    const payload = {
      slug: this.slug_value,
      draft: this.reviewDraft,
      mode,
    };
    this.http.postjson(this.BASE_URL.AI_REVIEW_REFINE, payload).subscribe(
      (res: Response) => {
        if (res.code === 200) {
          this.reviewDraft = res.data["review_draft"];
        } else {
          this.toastr.error("Couldn't refine the review. Try again.");
        }
        this.isRefining = false;
      },
      () => {
        this.toastr.error("Couldn't refine the review. Try again.");
        this.isRefining = false;
      }
    );
  }

  submit(): void {
    if (this.submitInFlight) return;
    if (this.sentiment === "negative") {
      this.submitNegativeFeedback();
      return;
    }
    this.copyAndPostToGoogle();
  }

  // Build the legacy review-rating payload mirroring review-improvement's
  // onSlugSelect(). Picks the right key (thumbs/faces/formal_rating) based on
  // feedback_scale + consumer_link, with an extreme value matching the
  // sentiment so the slug rating reflects positive/negative intent.
  private buildLegacyRatingPayload(sentiment: Sentiment): any {
    const scale = this.feedbackScale || "2";
    const isFormal = (this.consumerLink || "Formal") === "Formal";
    let thumbs: string | null = null;
    let faces: string | null = null;
    let formal_rating: string | null = null;
    let scale_type: string;

    if (scale === "10") {
      scale_type = "TEN_POINT_SCALE";
      formal_rating = sentiment === "positive" ? "10" : "1";
    } else if (scale === "5") {
      scale_type = "FIVE_POINT_SCALE";
      const value = sentiment === "positive" ? "5" : "1";
      if (isFormal) formal_rating = value;
      else faces = value;
    } else {
      scale_type = "TWO_POINT_SCALE";
      if (isFormal) {
        thumbs = sentiment === "positive" ? "thumbs_up" : "thumbs_down";
      } else {
        faces = sentiment === "positive" ? "5" : "1";
      }
    }

    return {
      slug: this.slug_value,
      thumbs,
      faces,
      formal_rating,
      review_scale_type: scale_type,
    };
  }

  // PUT /v1/review/update/actions — records the slug rating selection.
  private updateFeedbackRating(payload: any): Promise<boolean> {
    return new Promise((resolve) => {
      this.http
        .putRequest({
          customApiUrl: this.BASE_URL.REVIEW_RATING_API,
          data: payload,
        })
        .subscribe(
          () => resolve(true),
          () => resolve(false)
        );
    });
  }

  // POST /v1/review/slug/feedback — records optional feedback text + star rating.
  // Returns "ok" on success, "duplicate" when backend signals already-submitted
  // (code 202, mirrors review-improvement.component.ts:446-448), "error" otherwise.
  private updateFeedbackToZceppa(
    feedbackText: string,
    starRating: number
  ): Promise<"ok" | "duplicate" | "error"> {
    const payload = {
      slug: this.slug_value,
      feedback_text: feedbackText,
      feedback_star: starRating,
    };
    return new Promise((resolve) => {
      this.http
        .postRequest({
          customApiUrl: this.BASE_URL.SMS_SLUG_REQUEST_URL,
          data: payload,
        })
        .subscribe(
          (res: Response) => {
            if (!res) return resolve("error");
            if (res.code === 202) return resolve("duplicate");
            return resolve("ok");
          },
          () => resolve("error")
        );
    });
  }

  private async submitNegativeFeedback(): Promise<void> {
    const text = this.feedbackText.trim();
    if (!text) {
      this.toastr.error("Please enter feedback");
      return;
    }
    this.submitInFlight = true;
    const ratingPayload = this.buildLegacyRatingPayload("negative");
    const ratingOk = await this.updateFeedbackRating(ratingPayload);
    const textResult = await this.updateFeedbackToZceppa(text, 1);
    // Audit row alongside legacy submit, fire-and-forget.
    this.captureSlugSelection({
      feedback_text: text,
      posted_to_google: false,
    });
    this.submitInFlight = false;

    if (textResult === "duplicate") {
      this.toastr.info("Feedback already received. Our team is looking into it.");
      this.feedbackText = "";
      this.isFeedbackAlreadyGiven = true;
      this.handleNegativeThankYou();
      return;
    }

    if (!ratingOk || textResult === "error") {
      this.toastr.error("Couldn't submit your feedback. Please try again.");
      return;
    }

    this.feedbackText = "";
    this.handleNegativeThankYou();
  }

  // Legacy redirectToExternalPlatform() decision tree for the negative path:
  //   - reviewGateway ON  -> show the thank-you modal (user can opt out)
  //   - feedbackOnlyFlow ON -> stay on thank-you screen, no external redirect
  //   - else -> show thank-you screen (no external redirect for negative)
  private handleNegativeThankYou(): void {
    if (this.reviewGateway) {
      this.showThankYouModal = true;
      return;
    }
    this.isShowThankYouScreen = true;
  }

  closeThankYou(): void {
    this.showThankYouModal = false;
    // After dismissing the modal, drop into the inline thank-you screen so the
    // user lands somewhere meaningful instead of the empty feedback editor.
    this.isShowThankYouScreen = true;
  }

  copyAndPostToGoogle(): void {
    if (!this.reviewDraft.trim()) {
      this.toastr.info("Nothing to copy yet");
      return;
    }
    this.submitInFlight = true;

    // TODO: re-enable legacy rating + feedback writes on the positive path
    // once we've decided how the AI draft should be stored. For now only the
    // AI-flow audit row is captured.
    // const ratingPayload = this.buildLegacyRatingPayload("positive");
    // this.updateFeedbackRating(ratingPayload);
    // if (this.reviewDraft.trim()) {
    //   this.updateFeedbackToZceppa(this.reviewDraft.trim(), 5);
    // }
    this.captureSlugSelection({
      final_text: this.reviewDraft,
      posted_to_google: !this.feedbackOnlyFlow,
    });

    if (this.feedbackOnlyFlow) {
      this.copyToClipboard(this.reviewDraft);
      this.submitInFlight = false;
      this.isShowThankYouScreen = true;
      return;
    }

    const url = this.config?.google_review_url;
    if (!url) {
      this.copyToClipboard(this.reviewDraft);
      this.submitInFlight = false;
      this.toastr.warning("Google review link is not configured for this location.");
      this.isShowThankYouScreen = true;
      return;
    }

    // WhatsApp's webview drops the gesture context across the setTimeout, so
    // navigate synchronously here. See openInExternalBrowser.
    if (this.isWhatsAppBrowser()) {
      this.copyToClipboard(this.reviewDraft);
      this.toastr.success("Copied. Opening Google review page...");
      this.openInExternalBrowser(url);
      return;
    }

    this.copyToClipboard(this.reviewDraft);
    this.toastr.success("Copied. Opening Google review page...");
    setTimeout(() => {
      this.submitInFlight = false;
      window.location.replace(url);
    }, 300);
  }

  private isWhatsAppBrowser(): boolean {
    const ua = navigator.userAgent || "";
    return (
      /WhatsApp/i.test(ua) ||
      /\[WAiOS\//i.test(ua) ||
      /\[WABIZ\//i.test(ua) ||
      /WA4A\//i.test(ua)
    );
  }

  // Break out of WhatsApp's in-app webview into the system browser, where
  // clipboard paste and Google auth actually work.
  private openInExternalBrowser(url: string): void {
    if (!url) return;
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const stripped = url.replace(/^https?:\/\//, "");
    const fallback = encodeURIComponent(url);
    if (isAndroid) {
      window.location.href = `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
    } else {
      window.location.href = `x-safari-https://${stripped}`;
    }
  }

  private copyToClipboard(text: string): void {
    if (navigator.clipboard && (window as any).isSecureContext) {
      navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private fallbackCopy(text: string): void {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try {
      document.execCommand("copy");
    } catch {
      // best-effort only
    }
    document.body.removeChild(ta);
  }
}
